import { useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { headerLogo, headerLogoBig, governmentOfGujarat } from "@gyandiksha/shared-ui/assets";
import { Iconify } from "@repo/design-system/components/ui";
import { getAuthProps, getPortalSelectionAppUrl, signOut } from "@/services";
import "./Sidebar.scss";

const Sidebar = ({
  isMobileOpen, setIsMobileOpen, isCollapsed, setIsCollapsed,
  openMenu, setOpenMenu, flyout, setFlyout, menuConfig = []
}) => {

  const location = useLocation();
  const flyoutRef = useRef(null);
  const hideTimer = useRef(null);

  // Auto-open the parent menu whose child matches the current URL
  useEffect(() => {
    for (const section of menuConfig) {
      for (const item of section.items) {
        if (item.children?.some(
          (child) =>
            location.pathname === child.navigationPath ||
            location.pathname.startsWith(child.navigationPath + "/")
        )) {
          setOpenMenu(item.menuKey);
          return;
        }
      }
    }
  }, [location.pathname, menuConfig, setOpenMenu]);

  const toggleMenu = (menuKey) => {
    if (isCollapsed) return;
    setOpenMenu((prev) => (prev === menuKey ? null : menuKey));
  };

  const closeMobileSidebar = () => setIsMobileOpen(false);

  // Same logout target as UserProfileMenu: company users go back to the placement login
  const handleLogout = () => {
    const isCompanyUser = Boolean(getAuthProps()?.companyId);
    signOut(isCompanyUser ? getPortalSelectionAppUrl("placementManagement", "/login") : undefined);
  };

  const clearHideTimer = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
  };

  const startHideTimer = () => {
    hideTimer.current = setTimeout(() => setFlyout(null), 100);
  };

  // Hover on any menu item when collapsed → show flyout
  const handleMouseEnter = (e, item) => {
    if (!isCollapsed) return;
    clearHideTimer();
    const rect = e.currentTarget.getBoundingClientRect();
    setFlyout({
      menuKey: item.menuKey,
      top: rect.top,
      title: item.menuTitle,
      // children only if parent menu; null for simple links
      children: item.children || null,
      navigationPath: item.navigationPath || null,
    });
  };

  const handleMouseLeave = () => {
    if (!isCollapsed) return;
    startHideTimer();
  };

  const handleFlyoutEnter = () => {
    clearHideTimer();
  };

  const handleFlyoutLeave = () => {
    startHideTimer();
  };

  const isActiveRoute = (path) => {
    // Exact match for root paths to avoid prefix collision
    if (location.pathname === path) return true;

    // Only match sub-routes if path is more than just a segment
    // e.g. /scholarship-applications should match /scholarship-applications/1
    // but /scholarship should NOT match /scholarship-applications
    return location.pathname.startsWith(path + "/");
  };

  return (
    <>
      {isMobileOpen && (
        <div className="sidebar-overlay" onClick={closeMobileSidebar} />
      )}

      <aside className={`sidebar-container ${isMobileOpen ? "open" : ""} ${isCollapsed ? "collapsed" : ""}`}>
        {/* ── Header ── */}
        <div className="sidebar-header">
          <div className="company-info">
            <div className={`logo-container ${isCollapsed ? "header-logo" : "header-logo-big"}`}>
              {isCollapsed ?
                <img src={headerLogo} alt="Logo" />
                : <img src={headerLogoBig} alt="Logo" />
              }
            </div>
            {/* {!isCollapsed && (
              <div className="company-info-text">
                <div className="company-name">UCCHA-SHIKSHA SAMIKSHA KENDRA</div>
                <div className="company-plan">Higher Education Department - Gujarat</div>
              </div>
            )} */}
          </div>
        </div>

        {/* ── Nav ── */}
        <nav className="sidebar-nav">
          {menuConfig.map((section, index) => (
            <div key={index} className="sidebar-section">
              {/* {!isCollapsed && <div className="section-label">{section.label}</div>} */}
              {/* {isCollapsed && <div className="section-divider" />} */}

              {section.items.map((item) => (
                <div
                  key={item.menuKey}
                  className="menu-item-wrapper"
                  onMouseEnter={(e) => handleMouseEnter(e, item)}
                  onMouseLeave={handleMouseLeave}
                >
                  {item.children ? (
                    <>
                      <button
                        className={`menu-button ${openMenu === item.menuKey ? "active" : ""}`}
                        onClick={() => toggleMenu(item.menuKey)}
                      >
                        <div className="menu-icon-text">
                          <Iconify icon={item.menuIcon} width={18} />
                          {!isCollapsed && <span>{item.menuTitle}</span>}
                        </div>
                        {!isCollapsed && (
                          <Iconify
                            icon="lucide:chevron-down"
                            className={`chevron-icon ${openMenu === item.menuKey ? "rotate" : ""}`}
                            width={16}
                          />
                        )}
                      </button>

                      {/* Inline submenu — expanded only */}
                      {!isCollapsed && openMenu === item.menuKey && (
                        <div className="submenu">
                          {item.children.map((child) => (
                            <Link
                              key={child.menuKey}
                              to={child.navigationPath}
                              className={`submenu-link ${isActiveRoute(child.navigationPath) ? "active" : ""}`}
                              onClick={closeMobileSidebar}
                            >
                              <div className="submenu-item-content">
                                {child.menuIcon && <Iconify icon={child.menuIcon} width={16} />}
                                <span>{child.subMenuName}</span>
                              </div>
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.navigationPath}
                      className={`menu-button link ${isActiveRoute(item.navigationPath) ? "active" : ""}`}
                      onClick={closeMobileSidebar}
                    >
                      <div className="menu-icon-text">
                        <Iconify icon={item.menuIcon} width={18} />
                        {!isCollapsed &&
                          <span>
                            {item.menuTitle} {item.count > 0 && `(${item.count})`}
                          </span>}
                      </div>
                    </Link>
                  )}
                </div>
              ))}
            </div>
          ))}
        </nav>

        {/* ── Footer ── */}
        <div className="sidebar-footer">
          <button
            type="button"
            className="logout-button"
            onClick={handleLogout}
            title={isCollapsed ? "Log out" : undefined}
            aria-label="Log out"
          >
            {isCollapsed ? <Iconify icon="lucide:log-out" width={18} /> : <span>Log out</span>}
          </button>

          <div className="gov-branding">
            <img src={governmentOfGujarat} alt="Government of Gujarat" />
            {!isCollapsed && <span>Government Of Gujarat</span>}
          </div>
        </div>
      </aside>

      {/* ── Flyout panel — rendered for ALL items when collapsed ── */}
      {isCollapsed && flyout && (
        <div
          ref={flyoutRef}
          className={`sidebar-flyout ${flyout.children ? "has-children" : "label-only"}`}
          style={{ top: flyout.top }}
          onMouseEnter={handleFlyoutEnter}
          onMouseLeave={handleFlyoutLeave}
        >
          {/* Section title row (always shown) */}
          <div className="flyout-title">{flyout.title}</div>

          {/* Child links — only for parent menu items */}
          {flyout.children && flyout.children.map((child) => (
            <Link
              key={child.menuKey}
              to={child.navigationPath}
              className={`flyout-link ${isActiveRoute(child.navigationPath) ? "active" : ""}`}
              onClick={() => { setFlyout(null); closeMobileSidebar(); }}
            >
              <div className="flyout-item-content">
                {child.menuIcon && <Iconify icon={child.menuIcon} width={15} />}
                <span>{child.subMenuName}</span>
              </div>
            </Link>
          ))}

          {/* Single link — clicking navigates */}
          {/* {!flyout.children && flyout.navigationPath && (
            <Link
              to={flyout.navigationPath}
              className={`flyout-link single ${isActiveRoute(flyout.navigationPath) ? "active" : ""}`}
              onClick={() => { setFlyout(null); closeMobileSidebar(); }}
            >
              <span>Go to page →</span>
            </Link>
          )} */}
        </div>
      )}
    </>
  );
};

export default Sidebar;